import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Phone, ChevronDown, User, BookOpen, Building2, Newspaper } from "lucide-react";
import { useLang } from "@/i18n/LanguageProvider";
import { buildPath, alternatePath } from "@/lib/routes";
import { getIcon } from "@/lib/icons";
import { doctor, LOGO } from "@/data/site";
import { serviceCategories } from "@/data/services";
import { serviceSlugById } from "@/data/serviceDetails";
import { conditions } from "@/data/conditions";
import { AmbientSound } from "@/components/common/AmbientSound";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const DOCTOR_LINKS = [
  { key: "about", icon: User },
  { key: "publications", icon: BookOpen },
  { key: "practice", icon: Building2 },
  { key: "articles", icon: Newspaper },
];
const DOCTOR_KEYS = DOCTOR_LINKS.map((d) => d.key);

export function Navbar() {
  const { lang, t } = useLang();
  const { pathname } = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
    setMobileSection(null);
  }, [pathname]);

  const homePath = buildPath("home", lang);
  const bookPath = buildPath("book", lang);
  const otherPath = alternatePath(pathname, lang === "el" ? "en" : "el");

  const isActive = (key) => {
    const p = buildPath(key, lang);
    return pathname === p || (p !== homePath && pathname.startsWith(p));
  };
  const onPath = (p) => pathname === p;
  const serviceDetailPrefix = lang === "el" ? "/ypiresies/" : "/en/services/";
  const servicesActive = pathname.startsWith(serviceDetailPrefix);
  const doctorActive = DOCTOR_KEYS.some((k) => isActive(k));
  const conditionsActive = pathname.startsWith(lang === "el" ? "/pathiseis" : "/en/conditions");

  const specCategories = serviceCategories.filter((c) => c.id !== "diagnostics-visits" && c.id !== "prevention");
  const desktopSpecCategories = [...specCategories].sort((left, right) => {
    const order = ["diagnosis", "interventional", "eeg", "neuromodulation"];
    return order.indexOf(left.id) - order.indexOf(right.id);
  });
  const movedServices = serviceCategories.filter((c) => c.id === "diagnostics-visits" || c.id === "prevention");

  const linkCls = (active) =>
    `whitespace-nowrap rounded-md text-[14px] transition-colors hover:text-petrol focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-petrol focus-visible:ring-offset-2 ${active ? "text-petrol font-medium" : "text-ink-soft"}`;
  const mobileLinkCls = (active) =>
    `rounded-lg px-2 py-3 text-[17px] hover:bg-ice-soft hover:text-petrol ${active ? "text-petrol font-medium" : "text-ink"}`;
  const mobileSubCls = (active) =>
    `flex items-center gap-3 rounded-lg px-2 py-2.5 text-[15px] transition-colors hover:bg-ice-soft hover:text-petrol ${active ? "bg-ice-soft text-petrol font-medium" : "text-ink-soft"}`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-line/70 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85">
      <div className="mx-auto flex h-[72px] w-full max-w-[1600px] items-center justify-between gap-4 px-4 md:px-8">
        <Link to={homePath} data-testid="navbar-logo" className="flex min-w-0 items-center gap-2.5">
          <img
            src={LOGO.mark}
            alt=""
            aria-hidden="true"
            width="140"
            height="162"
            className="h-9 w-auto shrink-0"
          />
          <span className="flex min-w-0 flex-col leading-none">
            <span className="truncate font-serif text-[15px] font-medium tracking-tight text-ink sm:text-[17px] md:text-[20px]">
              {doctor.name[lang]}
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-petrol">{t("brand.role")}</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-x-6 xl:flex" aria-label="Primary">
          <Link to={homePath} className={linkCls(pathname === homePath)} data-testid="navlink-home">{t("nav.home")}</Link>

          {/* Services dropdown */}
          <div className="relative order-4" onMouseEnter={() => setOpenMenu("services")} onMouseLeave={() => setOpenMenu(null)}>
            <button
              className={`flex items-center gap-1 ${linkCls(servicesActive)}`}
              aria-haspopup="true"
              aria-expanded={openMenu === "services"}
              onClick={() => setOpenMenu(openMenu === "services" ? null : "services")}
              data-testid="navlink-services"
            >
              {t("nav.services")} <ChevronDown className={`h-4 w-4 transition-transform ${openMenu === "services" ? "rotate-180" : ""}`} strokeWidth={1.75} />
            </button>
            {openMenu === "services" && (
              <div className="absolute left-1/2 top-full z-50 w-[560px] -translate-x-1/2 pt-4" data-testid="services-dropdown">
                <div className="grid grid-cols-2 gap-1 rounded-xl border border-line bg-white p-3 shadow-[0_16px_50px_rgba(16,33,31,0.12)]">
                  {desktopSpecCategories.map((cat) => {
                    const Icon = getIcon(cat.icon);
                    return (
                      <Link
                        key={cat.id}
                        to={buildPath("serviceDetail", lang, { slug: serviceSlugById[cat.id][lang] })}
                        data-testid={`dropdown-service-${cat.id}`}
                        className="group flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-ice-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-petrol"
                      >
                        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-petrol-soft text-petrol transition-colors group-hover:bg-petrol group-hover:text-white">
                          <Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
                        </span>
                        <span>
                          <span className="block text-[14px] font-medium text-ink group-hover:text-petrol">{cat.title[lang]}</span>
                          <span className="mt-0.5 block text-[12px] leading-snug text-ink-soft line-clamp-2">{cat.summary[lang]}</span>
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* The Doctor dropdown */}
          <div className="relative order-3" onMouseEnter={() => setOpenMenu("doctor")} onMouseLeave={() => setOpenMenu(null)}>
            <button
              className={`flex items-center gap-1 ${linkCls(doctorActive)}`}
              aria-haspopup="true"
              aria-expanded={openMenu === "doctor"}
              onClick={() => setOpenMenu(openMenu === "doctor" ? null : "doctor")}
              data-testid="navlink-doctor"
            >
              {t("nav.doctor")} <ChevronDown className={`h-4 w-4 transition-transform ${openMenu === "doctor" ? "rotate-180" : ""}`} strokeWidth={1.75} />
            </button>
            {openMenu === "doctor" && (
              <div className="absolute left-0 top-full z-50 w-64 pt-4" data-testid="doctor-dropdown">
                <div className="rounded-xl border border-line bg-white p-2 shadow-[0_16px_50px_rgba(16,33,31,0.12)]">
                  {DOCTOR_LINKS.map(({ key, icon: Icon }) => (
                    <Link
                      key={key}
                      to={buildPath(key, lang)}
                      data-testid={`dropdown-${key}`}
                      className={`group flex items-center gap-3 rounded-lg px-3 py-3 text-[15px] transition-colors hover:bg-ice-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-petrol ${isActive(key) ? "text-petrol" : "text-ink"}`}
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-petrol-soft text-petrol transition-colors group-hover:bg-petrol group-hover:text-white">
                        <Icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
                      </span>
                      {t(`nav.${key}`)}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Conditions dropdown */}
          <div className="relative order-2" onMouseEnter={() => setOpenMenu("conditions")} onMouseLeave={() => setOpenMenu(null)}>
            <button
              className={`flex items-center gap-1 ${linkCls(conditionsActive)}`}
              aria-haspopup="true"
              aria-expanded={openMenu === "conditions"}
              onClick={() => setOpenMenu(openMenu === "conditions" ? null : "conditions")}
              data-testid="navlink-conditions"
            >
              {t("nav.conditionsServices")} <ChevronDown className={`h-4 w-4 transition-transform ${openMenu === "conditions" ? "rotate-180" : ""}`} strokeWidth={1.75} />
            </button>
            {openMenu === "conditions" && (
              <div className="absolute left-1/2 top-full z-50 w-[520px] -translate-x-1/2 pt-4" data-testid="conditions-dropdown">
                <div className="grid grid-cols-2 gap-1 rounded-xl border border-line bg-white p-3 shadow-[0_16px_50px_rgba(16,33,31,0.12)]">
                  {conditions.map((c) => {
                    const Icon = getIcon(c.icon);
                    return (
                      <Link
                        key={c.slug.en}
                        to={buildPath("conditionDetail", lang, { slug: c.slug[lang] })}
                        data-testid={`dropdown-condition-${c.slug.en}`}
                        className="group flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-ice-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-petrol"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-petrol-soft text-petrol transition-colors group-hover:bg-petrol group-hover:text-white">
                          <Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
                        </span>
                        <span className="text-[14px] font-medium leading-snug text-ink group-hover:text-petrol">{c.name[lang]}</span>
                      </Link>
                    );
                  })}
                  <Link
                    to={buildPath("faq", lang)}
                    data-testid="dropdown-faq"
                    className="group flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-ice-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-petrol"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-petrol-soft font-serif text-[17px] text-petrol transition-colors group-hover:bg-petrol group-hover:text-white">?</span>
                    <span className="text-[14px] font-medium leading-snug text-ink group-hover:text-petrol">{t("faq.title")}</span>
                  </Link>
                  <div className="col-span-2 mt-1.5 border-t border-line pt-2">
                    <p className="px-3 pb-0.5 text-[11px] font-medium uppercase tracking-[0.18em] text-ink-soft">{t("nav.servicesGroup")}</p>
                  </div>
                  {movedServices.map((cat) => {
                    const SvcIcon = getIcon(cat.icon);
                    return (
                      <Link
                        key={cat.id}
                        to={buildPath("serviceDetail", lang, { slug: serviceSlugById[cat.id][lang] })}
                        data-testid={`dropdown-service-${cat.id}`}
                        className="group flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-ice-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-petrol"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-petrol-soft text-petrol transition-colors group-hover:bg-petrol group-hover:text-white">
                          <SvcIcon className="h-[18px] w-[18px]" strokeWidth={1.5} />
                        </span>
                        <span className="text-[14px] font-medium leading-snug text-ink group-hover:text-petrol">{cat.title[lang]}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <Link to={buildPath("contact", lang)} className={`order-5 ${linkCls(isActive("contact"))}`} data-testid="navlink-contact">{t("nav.contact")}</Link>
        </nav>

        <div className="flex shrink-0 items-center gap-2.5">
          <AmbientSound className="hidden lg:inline-flex" />
          <div className="hidden items-center overflow-hidden rounded-full border-2 border-petrol/40 bg-white text-[13px] sm:flex">
            <Link to={lang === "el" ? pathname : otherPath} data-testid="lang-switch-el" className={`px-3 py-1.5 transition-colors ${lang === "el" ? "bg-petrol text-white" : "text-ink-soft hover:text-petrol"}`} aria-label="Ελληνικά">ΕΛ</Link>
            <Link to={lang === "en" ? pathname : otherPath} data-testid="lang-switch-en" className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-petrol text-white" : "text-ink-soft hover:text-petrol"}`} aria-label="English">EN</Link>
          </div>
          <Link
            to={bookPath}
            data-testid="navbar-book-btn"
            className="hidden items-center whitespace-nowrap rounded-full bg-petrol px-5 py-2 text-[14px] font-semibold text-white transition-colors hover:bg-petrol-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-petrol focus-visible:ring-offset-2 md:inline-flex"
          >
            {t("nav.book")}
          </Link>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="group inline-flex min-h-11 min-w-11 items-center justify-center gap-2.5 rounded-md px-1.5 xl:hidden"
                aria-label={t("nav.menu")}
              >
                <span className="hidden text-[11px] font-medium uppercase tracking-[0.22em] text-ink-soft transition-colors group-hover:text-petrol sm:inline">{t("nav.menu")}</span>
                <span className="flex flex-col items-end gap-[5px]" aria-hidden="true">
                  <span className="block h-px w-4 rounded-full bg-ink transition-all duration-300 ease-out group-hover:w-7 group-hover:bg-petrol" />
                  <span className="block h-px w-6 rounded-full bg-ink transition-all duration-300 ease-out group-hover:w-5 group-hover:bg-petrol" />
                  <span className="block h-px w-8 rounded-full bg-ink transition-all duration-300 ease-out group-hover:w-7 group-hover:bg-petrol" />
                </span>
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto bg-white p-0 [&>button]:hidden">
              <div className="flex h-[72px] items-center justify-between border-b border-line px-6">
                <SheetTitle className="flex items-center gap-2 font-serif text-lg font-medium text-ink">
                  <img
                    src={LOGO.mark}
                    alt=""
                    aria-hidden="true"
                    width="140"
                    height="162"
                    className="h-8 w-auto shrink-0"
                  />
                  {doctor.name[lang]}
                </SheetTitle>
                <SheetDescription className="sr-only">{t("nav.menu")}</SheetDescription>
                <SheetClose asChild>
                  <button data-testid="mobile-menu-close" className="inline-flex h-11 w-11 items-center justify-center rounded-md text-ink hover:bg-ice-soft" aria-label={t("reviews.close")}>
                    <X className="h-7 w-7" strokeWidth={1.5} />
                  </button>
                </SheetClose>
              </div>

              <nav className="flex flex-col px-4 py-4" aria-label="Mobile">
                <SheetClose asChild><Link to={homePath} data-testid="mobile-navlink-home" className={mobileLinkCls(pathname === homePath)}>{t("nav.home")}</Link></SheetClose>

                {/* Services collapsible */}
                <button
                  onClick={() => setMobileSection((s) => (s === "services" ? null : "services"))}
                  data-testid="mobile-services-toggle"
                  aria-expanded={mobileSection === "services"}
                  className={`order-4 flex items-center justify-between rounded-lg px-2 py-3 text-[17px] hover:bg-ice-soft ${servicesActive ? "text-petrol font-medium" : "text-ink"}`}
                >
                  {t("nav.services")} <ChevronDown className={`h-5 w-5 transition-transform ${mobileSection === "services" ? "rotate-180 text-petrol" : "text-ink-soft"}`} strokeWidth={1.75} />
                </button>
                {mobileSection === "services" && (
                  <div className="order-4 mb-1 space-y-0.5 pl-1" data-testid="mobile-services-panel">
                    {specCategories.map((cat) => {
                      const Icon = getIcon(cat.icon);
                      const p = buildPath("serviceDetail", lang, { slug: serviceSlugById[cat.id][lang] });
                      return (
                        <SheetClose asChild key={cat.id}>
                          <Link to={p} data-testid={`mobile-service-${cat.id}`} className={mobileSubCls(onPath(p))}>
                            <Icon className="h-4 w-4 shrink-0 text-petrol" strokeWidth={1.6} /> {cat.title[lang]}
                          </Link>
                        </SheetClose>
                      );
                    })}
                  </div>
                )}

                {/* Doctor collapsible */}
                <button
                  onClick={() => setMobileSection((s) => (s === "doctor" ? null : "doctor"))}
                  data-testid="mobile-doctor-toggle"
                  aria-expanded={mobileSection === "doctor"}
                  className={`order-3 flex items-center justify-between rounded-lg px-2 py-3 text-[17px] hover:bg-ice-soft ${doctorActive ? "text-petrol font-medium" : "text-ink"}`}
                >
                  {t("nav.doctor")} <ChevronDown className={`h-5 w-5 transition-transform ${mobileSection === "doctor" ? "rotate-180 text-petrol" : "text-ink-soft"}`} strokeWidth={1.75} />
                </button>
                {mobileSection === "doctor" && (
                  <div className="order-3 mb-1 space-y-0.5 pl-1" data-testid="mobile-doctor-panel">
                    {DOCTOR_LINKS.map(({ key, icon: Icon }) => (
                      <SheetClose asChild key={key}>
                        <Link to={buildPath(key, lang)} data-testid={`mobile-navlink-${key}`} className={mobileSubCls(isActive(key))}>
                          <Icon className="h-4 w-4 shrink-0 text-petrol" strokeWidth={1.6} /> {t(`nav.${key}`)}
                        </Link>
                      </SheetClose>
                    ))}
                  </div>
                )}

                {/* Conditions collapsible */}
                <button
                  onClick={() => setMobileSection((s) => (s === "conditions" ? null : "conditions"))}
                  data-testid="mobile-conditions-toggle"
                  aria-expanded={mobileSection === "conditions"}
                  className={`order-2 flex items-center justify-between rounded-lg px-2 py-3 text-[17px] hover:bg-ice-soft ${conditionsActive ? "text-petrol font-medium" : "text-ink"}`}
                >
                  {t("nav.conditionsServices")} <ChevronDown className={`h-5 w-5 transition-transform ${mobileSection === "conditions" ? "rotate-180 text-petrol" : "text-ink-soft"}`} strokeWidth={1.75} />
                </button>
                {mobileSection === "conditions" && (
                  <div className="order-2 mb-1 space-y-0.5 pl-1" data-testid="mobile-conditions-panel">
                    {conditions.map((c) => {
                      const Icon = getIcon(c.icon);
                      const p = buildPath("conditionDetail", lang, { slug: c.slug[lang] });
                      return (
                        <SheetClose asChild key={c.slug.en}>
                          <Link to={p} data-testid={`mobile-condition-${c.slug.en}`} className={mobileSubCls(onPath(p))}>
                            <Icon className="h-4 w-4 shrink-0 text-petrol" strokeWidth={1.6} /> {c.name[lang]}
                          </Link>
                        </SheetClose>
                      );
                    })}
                    <p className="mt-2 border-t border-line px-2 pb-0.5 pt-3 text-[12px] font-medium uppercase tracking-[0.16em] text-ink-soft">{t("nav.servicesGroup")}</p>
                    {movedServices.map((cat) => {
                      const SvcIcon = getIcon(cat.icon);
                      const sp = buildPath("serviceDetail", lang, { slug: serviceSlugById[cat.id][lang] });
                      return (
                        <SheetClose asChild key={cat.id}>
                          <Link to={sp} data-testid={`mobile-condserv-${cat.id}`} className={mobileSubCls(onPath(sp))}>
                            <SvcIcon className="h-4 w-4 shrink-0 text-petrol" strokeWidth={1.6} /> {cat.title[lang]}
                          </Link>
                        </SheetClose>
                      );
                    })}
                  </div>
                )}

                <SheetClose asChild>
                  <Link to={buildPath("contact", lang)} data-testid="mobile-navlink-contact" className={`order-5 ${mobileLinkCls(isActive("contact"))}`}>{t("nav.contact")}</Link>
                </SheetClose>

                <SheetClose asChild>
                  <Link to={bookPath} data-testid="mobile-book-btn" className="order-6 mt-5 inline-flex items-center justify-center rounded-full bg-petrol px-5 py-3.5 text-[16px] font-semibold text-white hover:bg-petrol-dark">{t("nav.book")}</Link>
                </SheetClose>
                <div className="order-7 mt-5 flex items-center gap-3">
                  <SheetClose asChild>
                    <Link
                      to={lang === "el" ? pathname : otherPath}
                      data-testid="mobile-lang-el"
                      className={`flex min-h-11 flex-1 items-center justify-center rounded-md border-2 text-center ${
                        lang === "el"
                          ? "border-petrol bg-petrol text-white"
                          : "border-petrol/40 text-ink-soft"
                      }`}
                    >
                      ΕΛ
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to={lang === "en" ? pathname : otherPath}
                      data-testid="mobile-lang-en"
                      className={`flex min-h-11 flex-1 items-center justify-center rounded-md border-2 text-center ${
                        lang === "en"
                          ? "border-petrol bg-petrol text-white"
                          : "border-petrol/40 text-ink-soft"
                      }`}
                    >
                      EN
                    </Link>
                  </SheetClose>
                  <AmbientSound />
                </div>
                <a
                  href={`tel:${doctor.phone}`}
                  data-testid="mobile-contact-phone"
                  className="order-8 mt-5 inline-flex min-h-11 items-center gap-2 px-2 text-ink-soft hover:text-petrol"
                >
                  <Phone className="h-4 w-4" strokeWidth={1.5} /> {doctor.phoneDisplay}
                </a>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
